import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import ClientsMarquee from "@/components/ClientsMarquee";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import { useT } from "@/lib/i18n";
import { ArrowRight, Linkedin, Check } from "lucide-react";

function initials(name) {
  return name.split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function Team() {
  const t = useT();
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/team").then(({ data }) => setMembers(data)).catch(() => {}); }, []);

  return (
    <Layout headerVariant="dark">
      {/* HERO */}
      <section data-testid="team-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="/brand/gradient_wedo.jpg" alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <p className="section-num text-accent-pink fade-up">{t("team.eyebrow")}</p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl fade-up-2">
            {t("team.titleA")}<span className="text-accent-yellow">{t("team.titleAccent")}</span>
          </h1>
          <p className="text-white/85 text-lg md:text-xl leading-relaxed mt-10 max-w-3xl fade-up-3">
            {t("team.lede")}
          </p>
        </div>
        <GradientStrip />
      </section>

      {/* TEAM GRID */}
      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {members.map((m, idx) => (
              <article
                key={m.id}
                data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="group bg-paper-2 relative overflow-hidden flex flex-col md:flex-row"
              >
                {/* color bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: m.color }} />

                {/* Photo */}
                <div className="md:w-2/5 shrink-0 relative bg-brand-deep min-h-[280px] md:min-h-[420px] overflow-hidden">
                  {m.photo ? (
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectPosition: m.photo_position || "50% 50%" }}
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${m.color}, #15104D)` }}
                    >
                      <span className="display text-white text-7xl md:text-8xl tracking-tight">
                        {initials(m.name)}
                      </span>
                    </div>
                  )}
                  <div className="absolute top-0 left-0 right-0 h-1 gradient-bar" />
                </div>

                {/* Content */}
                <div className="flex-1 p-8 md:p-10 flex flex-col">
                  <h2 className="display text-3xl md:text-4xl leading-tight">{m.name}</h2>
                  <p className="text-sm text-ink/70 mt-2 font-medium">{m.role}</p>

                  <p className="text-[14px] leading-relaxed text-ink/80 mt-5 flex-1">
                    {m.bio}
                  </p>

                  {m.expertise?.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {m.expertise.map((e) => (
                        <span key={e} className="text-[11px] uppercase tracking-wider px-2.5 py-1 border border-ink/15 text-ink/70">
                          {e}
                        </span>
                      ))}
                    </div>
                  )}

                  {m.certifications?.length > 0 && (
                    <ul className="mt-6 space-y-1.5">
                      {m.certifications.map((c) => (
                        <li key={c} className="flex items-start gap-2 text-[12px] text-ink/65 leading-snug">
                          <Check size={12} className="mt-1 shrink-0" style={{ color: m.color }} />
                          {c}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-6 flex items-center justify-between pt-5 border-t border-ink/10">
                    <p className="text-[12px] text-ink/70 uppercase tracking-wider">
                      {(m.languages || []).join(" · ")}
                    </p>
                    {m.linkedin && (
                      <a href={m.linkedin} target="_blank" rel="noreferrer"
                         className="text-ink/50 hover:text-brand transition-colors" data-testid={`team-li-${m.id}`}>
                        <Linkedin size={16}/>
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ClientsMarquee />

      {/* CTA */}
      <section className="bg-brand text-white py-24 md:py-32 grain relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="section-num text-white/85">{t("team.cta.eyebrow")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("team.cta.title")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="team-cta" className="btn-pill bg-white text-brand hover:bg-paper-2">
              {t("common.cta.bookCall")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
      <GradientStrip height="h-2"/>
    </Layout>
  );
}
